<template>
  <header class="header--wrap">
    <div class="header layout-transition-left">
      <logo />
      <ul class="navigation">
        <li v-for="(item, index) in myInfoPage" :key="`user-info-${index}`"  :class="[item.name]">
          <button :class="{ active: currentMenu === item.name , disabled: item.disabled }"
                  @click="go({ name: item.name }, item.disabled)">
            {{ $t(`userInfo.lnb.${item.name}`) }}
          </button>
          <template v-for="(sub, idx) in item.list">
            <div :key="`user-info-sub${idx}`"
                :class="{ active: currentMenu === sub.name }"
                @click="go({ name: sub.name }, sub.disabled)">
                {{ $t(`userInfo.lnb.${sub.name}`) }}
                <ui-icon-tooltip v-if="expireJamList.length > 0 && sub.name === 'jam'"
                             :iconSize="'yellow'"
                             arrowBottom
                             class="icon"
                             arrowCenter
                             :control="true">
                <span v-html="$t('common.expireJam')"></span>
                </ui-icon-tooltip>
            </div>
          </template>
        </li>
      </ul>
    </div>
    <div class="user-info layout-transition-up">
      <div class="title--area">
        <transition name="title">
          <p v-html="userInfoSummary"></p>
        </transition>
      </div>

      <div class="button--area clearfix" v-if="userData">
        <battery-indicator
          :description="pvDescription"/>

        <ui-button class="charge-pv"
                   v-if="creditData.percentage <= 30"
                   :disabled="!creditData.canChargePv"
                   :backgroundColor="'#1199af'"
                   :primary4="true"
                   :size="'small'"
                   :iconName="'icon--pv-plug'"
                   @click="onChargePv">
          {{ $t('common.chargePv') }}
        </ui-button>

        <button class="user-menu" @click.stop.prevent="toggleUserMenu">
          <i class="icon icon--user"></i>
        </button>
        <transition name="slide">
          <user-menu v-if="showUserMenu" />
        </transition>
      </div>
    </div>
  </header>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import { format, addDays } from 'date-fns'
import { getExpireJamList } from '@/api/jam'
import UiButton from '@/components/ui/UiButton'
import BatteryIndicator from '@/components/header/BatteryIndicator'
import UserMenu from '@/components/header/UserMenu'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import Logo from '@/components/common/Logo.vue'

export default {
  name: 'UserInfoHeader',
  components: {
    Logo,
    UiButton,
    BatteryIndicator,
    UserMenu,
    UiIconTooltip
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'showUserMenu'
    ]),
    ...mapGetters([
      'creditData'
    ]),
    userInfoSummary () {
      const planData = this.planData
      let summary
      let planName = planData.planName

      if (planName && this.creditData.hasPlan) {
        summary = this.$t('gnb.plan.usingPlan', {
          userName: this.userData.userId,
          planName: planName
        })
      } else {
        summary = this.$t('gnb.plan.usingNoPlan', {
          userName: this.userData.userId
        })
      }
      return summary
    },
    pvDescription () {
      if (!this.creditData.hasPlan) {
        return this.$t('gnb.battery.noUsingPlan')
      } else if (this.creditData.isUnlimited) {
        return this.$t('gnb.battery.noLimit')
      } else if (!this.creditData.remain) {
        return this.$t('gnb.battery.empty')
      } else {
        return this.$t('gnb.battery.available', {
          pv: this.$options.filters.thousand(this.creditData.remain)
        })
      }
    },
    myInfoPage () {
      return [
        { name: 'managePlan' },
        { name: 'billing' },
        { name: 'ipBlocking' },
        { name: 'jam' },
        { name: 'myInformation', list: [{ name: 'referrals' }] }
      ]
    }
  },
  data () {
    return {
      currentMenu: null,
      subMenu: null,
      expireJamList: []
    }
  },
  watch: {
    '$route.name' (value) {
      this.currentMenu = value
    }
  },
  created () {
    this.setExpireJamList()
    EventBus.$on('setTitle', (value) => {
      this.title = value
    })
    this.currentMenu = this.$route.name
  },
  mounted () {
    EventBus.$on('closeUserMenu', () => {
      this.setShowUserMenu(false)
    })
  },
  methods: {
    ...mapMutations([
      'setShowUserMenu',
      'clearAllUserData'
    ]),
    toDashboard () {
      if (this.$route.name !== 'dashboard') {
        EventBus.$emit('onLoading', true)
        this.$router.push({ name: 'dashboard' })
      }
    },
    go (params, stop) {
      if (params.name !== this.currentMenu && !stop) {
        EventBus.$emit('onLoading', true)
        this.currentMenu = params.name
        setTimeout(() => {
          this.$router.push(params)
        }, 300)
      }
    },
    toggleUserMenu () {
      EventBus.$emit('closeAllUiSelectBox')
      this.setShowUserMenu(!this.showUserMenu)
      this.showLanguageListStatus = false
    },
    onChargePv () {
      if (this.$route.name === 'managePlan') {
        EventBus.$emit('onActionTrigger', 'chargePv')
      } else {
        this.$router.push({ name: 'managePlan', params: { action: 'chargePv' } })
      }
    },
    setExpireJamList () {
      getExpireJamList({ expireDate: format(addDays(new Date(), 365), 'YYYY-MM-DD') }).then(res => {
        this.expireJamList = res.remainList
      }).catch(err => {
        console.log(err)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.header--wrap {
  position: relative;
  z-index: 100;

  .header {
    position: fixed;
    z-index: 3;
    width: 210px;
    height: 100%;
    box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.3), 0 3px 6px 0 rgba(0, 0, 0, 0.08);
    background-color: #ffffff;

    .logo {
      vertical-align: top;
      display: inline-block;
      position: relative;
      margin: 15px 24px 15px 9px;
      z-index: 10;
      cursor: pointer;

      .icon--logo-red {
        width: 112px;
        height: 35px;
        margin: -3px 0 0 3px;
      }

      h2 {
          margin-top: 5px;
          font-family: "rubrik-new";
          font-size: 14px;
          font-weight: bold;
          font-stretch: normal;
          font-style: normal;
          line-height: 1;
          letter-spacing: -0.22px;
          color: #ec0047;
      }
    }

    .navigation {
      margin-top: 54px;
      border-top: 1px solid #ebebeb;

      li {
        border-bottom: 1px solid #ebebeb;

        &.ipBlocking {
          border-bottom: none;
          position: relative;
          padding-bottom: 6px;

          &:before {
            content: '';
            position: absolute;
            bottom: 0;
            width: 100%;
            height: 4px;
            border-top:1px solid #E0E0E0;
            border-bottom:1px solid #E0E0E0;
            background-color: #F4F4F4;
          }
        }

        div {
          position: relative;
          .icon {
            position: absolute;
            top: -3px;
            left: 53px;
            & /deep/ .bubble {
              left: 89px;
              margin-bottom: 3px;
            }
            & /deep/ .arrow {
              left: 15%;
            }
          }
        }

        button {
          display: block;
          padding: 12px;
          height: 50px;
          width: 100%;
          text-align: left;
          line-height: 26px;
          font-size: 13px;
          color: #2f2f2f;
          font-weight: 500;
          transition: color 0.3s ease;

          &.active {
            color: #ec0047;
          }

          &.disabled {
            cursor: default;
            opacity: 0.4;
          }
        }
      }

      div {
        font-size: 13px;
        padding: 0px 12px 15px 12px;
        color: #444444;
        cursor: pointer;

        &.active {
            color: #ec0047;
        }
      }

      div::before {
        content: "•";
        color: #999999;
      }
    }
  }

  .user-info {
    margin-left: 230px;

    .title--area {
      position: relative;
      vertical-align: top;
      display: inline-block;
      height: 47px;
      font-size: 13px;
      white-space: nowrap;

      h2 {
        position: absolute;
        top: 0;
        left: 0;
        font-size: 22px;
        color: #444444;
        padding: 25px 0 0 15px;
        font-weight: 500;
      }

      p {
        position: absolute;
        top: 0;
        left: 0;
        padding: 19px 0 0 21px;
      }

      /deep/ {
        .user-name {
          color: #444444;
          font-size: 14px;
        }

        .plan-name {
          color: #ec0047;
        }
      }
    }

    .button--area {
      position: absolute;
      z-index: 30;
      top: 11px;
      right: 15px;
      transform: none;

      button.user-menu {
        width: 30px;
        height: 30px;
        border-radius: 15px;
        display: inline-block;
        vertical-align: middle;
        transition: background-color 0.2s ease;
        margin: 0 10px 0 15px;

        .icon--user {
          margin: 0;
        }

        &:hover {
          background-color: rgba(0, 0, 0, 0.06);
        }
      }

      button.charge-pv {
        margin-top: -2px;
        margin-left: 12px;
        transform: translateY(1px);
      }
    }
  }

  .title-enter-active, .title-leave-active {
    transition: transform 0.5s ease,
                opacity 0.5s ease;
  }
  .title-enter{
    transform: translateY(-50px);
    opacity: 0;
  }
  .title-leave-to{
    transform: translateY(30px);
    opacity: 0;
  }
}
</style>
